import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import swal from 'sweetalert';

const ImageUploadWithCrop = ({
  name,
  label,
  required = false,
  onChange,
  value,
  error,
  placeholder = "Choose file",
  maxSize = 2 * 1024 * 1024, // 2MB default
  acceptedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif", "image/bmp", "image/tiff"],
  aspectRatio = null, // Set to a number for fixed aspect ratio, null for free crop
  className = "form-control",
  onPreviewClick
}) => {
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);

  // Convert file to data URL
  const getFileDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Simple and reliable crop function
  const getCroppedImg = (image, crop, fileName) => {
    return new Promise((resolve, reject) => {
      try {
        // Create canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
          reject(new Error('Canvas not supported'));
          return;
        }

        // Get image dimensions
        const { naturalWidth, naturalHeight } = image;
        const { width: displayWidth, height: displayHeight } = image;
        
        // Calculate scale factors
        const scaleX = naturalWidth / displayWidth;
        const scaleY = naturalHeight / displayHeight;
        
        // Calculate crop area in actual image pixels
        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;
        const cropWidth = crop.width * scaleX;
        const cropHeight = crop.height * scaleY;
        
        // Set canvas size to crop size
        canvas.width = cropWidth;
        canvas.height = cropHeight;
        
        // Draw the cropped portion
    ctx.drawImage(
      image,
          cropX, cropY, cropWidth, cropHeight,  // Source rectangle
          0, 0, cropWidth, cropHeight           // Destination rectangle
        );
        
        // Convert to blob
      canvas.toBlob(
        (blob) => {
            if (blob) {
          blob.name = fileName;
          resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
        },
        'image/jpeg',
        0.9
      );
      } catch (error) {
        reject(error);
      }
    });
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize) {
      swal("Error!", `File size should not exceed ${Math.round(maxSize / (1024 * 1024))}MB.`, "error");
      e.target.value = '';
      return;
    }

    // Validate file type
    if (!acceptedTypes.includes(file.type)) {
      swal("Error!", `Only ${acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')} files are allowed.`, "error");
      e.target.value = '';
      return;
    }

    try {
      setIsLoading(true);
      setSelectedFile(file);
      const dataUrl = await getFileDataUrl(file);
      setImageUrl(dataUrl);
      setShowCropModal(true);
      
      // Reset crop with default values - 92x92 square
      setCrop({
        unit: 'px',
        width: 92,
        height: 92,
        x: 0,
        y: 0,
        aspect: 1
      });
    } catch (error) {
      console.error('Error processing file:', error);
      swal("Error!", "Error processing file. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image load
  const onImageLoad = useCallback((img) => {
    imgRef.current = img;
    console.log('Image loaded:', img.naturalWidth, 'x', img.naturalHeight);
    
    // Set initial crop area when image loads
    const imageWidth = img.width;
    const imageHeight = img.height;
    const cropSize = Math.min(imageWidth, imageHeight) * 0.8; // 80% of smaller dimension
    const cropX = (imageWidth - cropSize) / 2;
    const cropY = (imageHeight - cropSize) / 2;
    
    const initialCrop = {
      unit: 'px',
      width: cropSize,
      height: cropSize,
      x: cropX,
      y: cropY,
      aspect: 1
    };
    
    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  }, []);

  // Handle crop change
  const handleCropChange = (crop) => {
    setCrop(crop);
  };

  // Handle crop complete
  const handleCropComplete = (crop) => {
    console.log('Crop completed:', crop);
    if (crop && crop.width && crop.height) {
      setCompletedCrop(crop);
    } else {
      setCompletedCrop(null);
    }
  };

  // Apply crop and save
  const handleApplyCrop = async () => {
    // Validate image reference
    if (!imgRef.current) {
      swal("Error!", "No image loaded. Please try again.", "error");
      return;
    }

    // Validate crop area - check if completedCrop exists and has valid dimensions
    if (!completedCrop || typeof completedCrop !== 'object') {
      swal("Error!", "Please select an area to crop by dragging on the image.", "error");
      return;
    }

    if (!completedCrop.width || !completedCrop.height || completedCrop.width <= 0 || completedCrop.height <= 0) {
      swal("Error!", "Please select a valid crop area.", "error");
      return;
    }

    if (completedCrop.width < 10 || completedCrop.height < 10) {
      swal("Error!", "Crop area is too small. Please select a larger area.", "error");
      return;
    }

    try {
      setIsLoading(true);
      
      // Ensure image is loaded
      if (!imgRef.current.complete || imgRef.current.naturalWidth === 0) {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Image load timeout')), 5000);
          imgRef.current.onload = () => {
            clearTimeout(timeout);
            resolve();
          };
          imgRef.current.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Image load failed'));
          };
        });
      }

      console.log('Starting crop with:', {
        image: `${imgRef.current.naturalWidth}x${imgRef.current.naturalHeight}`,
        crop: completedCrop
      });

      const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop, selectedFile.name);
      
      if (!croppedImageBlob || croppedImageBlob.size === 0) {
        throw new Error('Failed to create cropped image');
      }
      
      console.log('Cropped image created successfully, size:', croppedImageBlob.size);
      
      // Create a new file from the cropped blob
      const croppedFile = new File([croppedImageBlob], selectedFile.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Update the form
      const event = {
        target: {
          name: name,
          files: [croppedFile]
        }
      };
      onChange(event);

      // Create preview URL for the cropped image
      const previewUrl = URL.createObjectURL(croppedImageBlob);
      setCroppedImageUrl(previewUrl);

      setShowCropModal(false);
      swal("Success!", "Image cropped successfully!", "success");
      console.log('Crop completed successfully');
    } catch (error) {
      console.error('Error cropping image:', error);
      
      let errorMessage = "Error cropping image. Please try again.";
      
      if (error.message.includes('Image load timeout')) {
        errorMessage = "Image took too long to load. Please try again.";
      } else if (error.message.includes('Image load failed')) {
        errorMessage = "Failed to load image. Please try with a different image.";
      } else if (error.message.includes('Canvas not supported')) {
        errorMessage = "Your browser doesn't support image processing. Please try a different browser.";
      } else if (error.message.includes('Failed to create blob')) {
        errorMessage = "Failed to process the image. Please try with a different image.";
      } else if (error.message.includes('Failed to create cropped image')) {
        errorMessage = "The cropped image is invalid. Please try again.";
      }
      
      swal("Error!", errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel crop
  const handleCancelCrop = () => {
    setShowCropModal(false);
    setImageUrl(null);
    setSelectedFile(null);
    setCrop(null);
    setCompletedCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="form-group">
        {label && (
          <label className="mb-2">
            <strong>
              {label}
              {required && <span className="text-red">*</span>}
            </strong>
          </label>
        )}
        <div className="input-group">
          <input
            ref={fileInputRef}
            type="file"
            name={name}
            className={`${className} ${error ? 'is-invalid' : ''}`}
            onChange={handleFileSelect}
            accept={acceptedTypes.join(',')}
            required={required}
            disabled={isLoading}
            style={{ height: "40px" }}
          />
          {(croppedImageUrl || value) && (
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                const previewUrl = croppedImageUrl || value;
                if (onPreviewClick && previewUrl) {
                  onPreviewClick(previewUrl, label);
                }
              }}
              disabled={isLoading}
              style={{ height: "40px" }}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                'Preview'
              )}
            </button>
          )}
        </div>
        {error && (
          <div className="invalid-feedback">{error}</div>
        )}
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Crop Image</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelCrop}
                  disabled={isLoading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <ReactCrop
                    crop={crop}
                    onChange={handleCropChange}
                    onComplete={handleCropComplete}
                    onImageLoaded={onImageLoad}
                    aspect={1}
                    className="img-fluid"
                  >
                    <img
                      ref={imgRef}
                      src={imageUrl}
                      alt="Crop preview"
                      style={{ maxWidth: '100%', maxHeight: '400px' }}
                      onLoad={() => console.log('Image onLoad event fired')}
                    />
                  </ReactCrop>
                </div>
                <div className="mt-3">
                  <small className="text-muted">
                    Drag to move the crop area. Use the corners to resize. 
                    Maintains 1:1 aspect ratio (square).
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelCrop}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleApplyCrop}
                  disabled={isLoading || !completedCrop?.width || !completedCrop?.height}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    'Apply Crop'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageUploadWithCrop;